import { FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resendEmailVerification, verifyEmailOtp } from "../API/auth";
import { setSignedIn } from "../utils/authStorage";
import {
  clearEmailVerificationState,
  getEmailVerificationState,
  setEmailVerificationState,
} from "../utils/emailVerificationStorage";
import { consumePostLoginRedirect } from "../utils/postLoginRedirect";
import {
  formatVerificationTime,
  verificationSecondsRemaining,
  verificationServerOffset,
} from "../utils/emailVerificationTime";
import { verifyEmailCopy } from "../utils/verifyEmailCopy";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(getEmailVerificationState);
  // Sign-in confirmation or new-address verification: same flow, different words.
  const copy = verifyEmailCopy(state?.reason);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(
    location.state?.emailDeliveryError ? copy.deliveryFailed : "",
  );
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [, forceTick] = useState(0);

  const serverOffset = useMemo(
    () =>
      state
        ? verificationServerOffset(state.serverTime, state.clientReceivedAt)
        : 0,
    [state],
  );
  const expiresIn = state
    ? verificationSecondsRemaining(state.expiresAt, serverOffset, Date.now())
    : 0;
  const resendIn = state
    ? verificationSecondsRemaining(state.resendAvailableAt, serverOffset, Date.now())
    : 0;

  useEffect(() => {
    if (!state) {
      navigate("/", { replace: true, state: { verificationSessionExpired: true } });
      return () => {};
    }
    const timer = window.setInterval(() => forceTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [navigate, state]);

  if (!state) return null;

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the six-digit verification code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await verifyEmailOtp(state.token, otp);
      // Task 149: verifying the code establishes the HttpOnly session
      // cookie; nothing is stored here.
      setSignedIn();
      localStorage.setItem("email", response.data.user?.email || state.email);
      localStorage.setItem("userDetails", JSON.stringify(response.data.user));
      clearEmailVerificationState();
      const deepLink = consumePostLoginRedirect();
      const hasEligibility = localStorage.getItem("visaEligibilityAnswers") || localStorage.getItem("visaEligibilityResponses");
      navigate(deepLink || (hasEligibility ? "/answer-review" : "/dashboard"), { replace: true });
    } catch (requestError: any) {
      const data = requestError?.response?.data;
      setError(data?.error || copy.failed);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await resendEmailVerification(state.token);
      const next = {
        ...state,
        expiresAt: response.data.expiresAt,
        resendAvailableAt: response.data.resendAvailableAt,
        serverTime: response.data.serverTime,
        clientReceivedAt: Date.now(),
      };
      setState(next);
      setEmailVerificationState(next);
      setMessage(copy.resent);
    } catch (requestError: any) {
      const data = requestError?.response?.data;
      if (data?.expiresAt && data?.resendAvailableAt && data?.serverTime) {
        const next = {
          ...state,
          expiresAt: data.expiresAt,
          resendAvailableAt: data.resendAvailableAt,
          serverTime: data.serverTime,
          clientReceivedAt: Date.now(),
        };
        setState(next);
        setEmailVerificationState(next);
      }
      setError(data?.error || "The verification email could not be sent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <h1 className="mb-1 text-center">{copy.heading}</h1>
      <p className="mb-8 text-center">{copy.intro}<strong>{state.email}</strong>.</p>
      <form className="mx-auto w-full max-w-[500px] rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-[35px]" onSubmit={handleVerify}>
        <label className="mb-2 flex text-sm font-semibold" htmlFor="verification-code">Verification code</label>
        <input
          id="verification-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otp}
          onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full rounded-lg border border-gray-200 px-4 py-3 text-center text-2xl tracking-[0.35em]"
          aria-describedby="verification-status"
        />
        <div id="verification-status" className="mt-3 min-h-6 text-sm" aria-live="polite">
          {error ? <span className="text-red-600">{error}</span> : message ? <span className="text-green-700">{message}</span> : <span className="text-gray-600">Code expires in {formatVerificationTime(expiresIn)}</span>}
        </div>
        <button type="submit" disabled={loading || expiresIn === 0} className="mt-5 w-full rounded-lg bg-[#1B365D] px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
          {loading ? "Please wait…" : copy.submit}
        </button>
        <button type="button" onClick={handleResend} disabled={loading || resendIn > 0} className="mt-3 w-full rounded-lg border border-[#1B365D] px-5 py-3 font-semibold text-[#1B365D] disabled:cursor-not-allowed disabled:opacity-50">
          {resendIn > 0 ? `Resend code in ${formatVerificationTime(resendIn)}` : "Resend code"}
        </button>
      </form>
    </div>
  );
}
